import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { config } from '@/lib/config';

interface AdminUser extends User {
  isAdmin?: boolean;
  role?: string;
  customClaims?: {
    isAdmin?: boolean;
    role?: string;
  };
}

interface AdminAuthContextType {
  user: AdminUser | null;
  loading: boolean;
  isAdmin: boolean;
  isRootAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: React.ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = async () => {
    if (user) {
      try {
        // Force token refresh to get updated custom claims
        await user.getIdToken(true);
        
        // Get the updated token result with custom claims
        const tokenResult = await user.getIdTokenResult();
        
        // Update user with custom claims
        const updatedUser = {
          ...user,
          customClaims: tokenResult.claims,
          isAdmin: tokenResult.claims.isAdmin || false,
          role: tokenResult.claims.role || 'user'
        };
        
        setUser(updatedUser);
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get token with custom claims
      const tokenResult = await user.getIdTokenResult();

      // Check if user is admin
      if (!tokenResult.claims.isAdmin) {
        await signOut(auth);
        throw new Error('Access denied. Admin privileges required.');
      }

      // Sync user to MongoDB
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${config.api.baseUrl}/api/sync-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: idToken }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.warn('Failed to sync user to MongoDB:', errorData.message);
        } else {
          const syncData = await response.json();
          console.log('User synced to MongoDB:', syncData.data?.user?.email);
        }
      } catch (syncError) {
        console.warn('User sync failed:', syncError);
        // Don't fail login if sync fails
      }

      // User will be set by the auth state listener
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get token with custom claims
          const tokenResult = await firebaseUser.getIdTokenResult();
          
          // Check if user has admin privileges
          const isAdmin = tokenResult.claims.isAdmin || false;
          const role = tokenResult.claims.role || 'user';
          
          if (isAdmin) {
            const adminUser: AdminUser = {
              ...firebaseUser,
              customClaims: tokenResult.claims,
              isAdmin,
              role
            };
            setUser(adminUser);
          } else {
            // User is not admin, sign them out
            await signOut(auth);
            setUser(null);
          }
        } catch (error) {
          console.error('Error getting user token:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const isAdmin = user?.isAdmin || false;
  const isRootAdmin = user?.role === 'root_admin';

  const value: AdminAuthContextType = {
    user,
    loading,
    isAdmin,
    isRootAdmin,
    login,
    logout,
    refreshToken
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Admin API utilities
export const adminAPI = {
  async request(endpoint: string, options: RequestInit = {}, retries = 2) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }

    try {
      const token = await user.getIdToken(true); // Force refresh token

      const response = await fetch(`${config.api.baseUrl}/api/admin${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          errorMessage = response.statusText || errorMessage;
        }

        // If it's a 401 and we have retries left, try again with fresh token
        if (response.status === 401 && retries > 0) {
          console.log('Retrying request with fresh token...');
          return this.request(endpoint, options, retries - 1);
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      // If it's a network error and we have retries left, try again
      if (retries > 0 && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        console.log('Retrying request due to network error...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return this.request(endpoint, options, retries - 1);
      }

      throw error;
    }
  },

  // Dashboard & Analytics
  async getDashboardAnalytics() {
    return this.request('/analytics');
  },

  // User Management
  async getUsers(params: Record<string, any> = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/users?${queryString}`);
  },

  async getUserDetails(userId: string) {
    return this.request(`/users/${userId}`);
  },

  async toggleUserBlock(userId: string, isBlocked: boolean, reason?: string) {
    return this.request(`/users/${userId}/block`, {
      method: 'PATCH',
      body: JSON.stringify({ isBlocked, reason }),
    });
  },

  async deleteUser(userId: string, reason?: string) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  },

  async promoteToAdmin(email: string) {
    return this.request('/users/promote', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async demoteAdmin(email: string) {
    return this.request('/users/demote', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Admin Management
  async getAdmins() {
    return this.request('/admins');
  },

  // Activity Logs
  async getActivityLogs(params: Record<string, any> = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/logs?${queryString}`);
  },

  // Settings
  async getSettings(category?: string) {
    const queryString = category ? `?category=${category}` : '';
    return this.request(`/settings${queryString}`);
  },

  async updateSetting(key: string, value: any) {
    return this.request(`/settings/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    });
  },

  async toggleMaintenanceMode(enabled: boolean, reason?: string) {
    return this.request('/settings/maintenance', {
      method: 'POST',
      body: JSON.stringify({ enabled, reason }),
    });
  },

  async updateUserLimits(limits: Record<string, number>) {
    return this.request('/settings/limits', {
      method: 'POST',
      body: JSON.stringify({ limits }),
    });
  },

  async resetUserCounters(userIds?: string[], counterType: string = 'all') {
    return this.request('/settings/reset-counters', {
      method: 'POST',
      body: JSON.stringify({ userIds, counterType }),
    });
  },

  // Support
  async getSupportRequests(params: Record<string, any> = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/support?${queryString}`);
  },

  async getSupportStats() {
    return this.request('/support/stats');
  },

  async getSupportRequestDetails(requestId: string) {
    return this.request(`/support/${requestId}`);
  },

  async assignSupportRequest(requestId: string, adminId: string) {
    return this.request(`/support/${requestId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ adminId }),
    });
  },

  async addSupportResponse(requestId: string, message: string) {
    return this.request(`/support/${requestId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  async updateSupportRequestStatus(requestId: string, status: string) {
    return this.request(`/support/${requestId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  // Blog Management
  async getBlogs(params: Record<string, any> = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/blogs?${queryString}`);
  },

  async getBlogDetails(blogId: string) {
    return this.request(`/blogs/${blogId}`);
  },

  async createBlog(blogData: any) {
    return this.request('/blogs', {
      method: 'POST',
      body: JSON.stringify(blogData),
    });
  },

  async updateBlog(blogId: string, blogData: any) {
    return this.request(`/blogs/${blogId}`, {
      method: 'PUT',
      body: JSON.stringify(blogData),
    });
  },

  async deleteBlog(blogId: string) {
    return this.request(`/blogs/${blogId}`, {
      method: 'DELETE',
    });
  },

  async updateBlogStatus(blogId: string, status: string) {
    return this.request(`/blogs/${blogId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};
